import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DollarSign, ShoppingBag, ChefHat, Package, Users, AlertTriangle, TrendingUp } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const STAT = (icon, label, value, sub, color = "#D4AF37") => ({ icon, label, value, sub, color });

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="text-[#FAF9F6]/50">Loading...</div>;
  const stats = [
    STAT(DollarSign, "Today's Sales", `$${d.today_total.toFixed(2)}`, `${d.orders_today} orders`),
    STAT(ChefHat, "Restaurant Sales", `$${d.restaurant_sales.toFixed(2)}`, "Today"),
    STAT(Package, "Grocery Sales", `$${d.grocery_sales.toFixed(2)}`, "Today"),
    STAT(ShoppingBag, "Pending Orders", d.pending_orders, "in pipeline"),
    STAT(ChefHat, "Kitchen Queue", d.kitchen_count, "in queue"),
    STAT(Package, "Grocery Picking", d.grocery_count, "in queue"),
    STAT(Users, "Customers", d.customers_count, "total"),
    STAT(AlertTriangle, "Low Stock", d.low_stock_count, "items", "#E8A87C"),
  ];
  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center gap-3 mb-6">
        <BrandLogo type="admin" size="sm" showText={false} />
        <div>
          <h1 className="font-heading text-3xl text-[#FAF9F6]">Royal Control Panel</h1>
          <p className="text-sm text-[#FAF9F6]/50">Maharlika Seafood Mart & Chow · Lancaster, CA</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s, i) => (
          <div key={i} className="admin-card p-5" data-testid={`stat-${i}`}>
            <s.icon className="w-5 h-5 mb-3" style={{ color: s.color }} strokeWidth={1.5}/>
            <div className="text-xs uppercase font-accent text-[#FAF9F6]/60 tracking-wider">{s.label}</div>
            <div className="font-heading text-3xl text-[#FAF9F6] mt-1">{s.value}</div>
            <div className="text-xs text-[#FAF9F6]/40">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 admin-card p-6">
          <h3 className="font-heading text-xl text-[#D4AF37] mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4"/> Sales last 7 days</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.sales_by_day}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#22272f" />
                <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 11 }} />
                <YAxis stroke="#666" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0B0E14", border: "1px solid #D4AF37" }} />
                <Area type="monotone" dataKey="total" stroke="#D4AF37" fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="admin-card p-6">
          <h3 className="font-heading text-xl text-[#D4AF37] mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4"/> Low Stock</h3>
          <ul className="space-y-2">
            {(d.low_stock || []).map((p) => (
              <li key={p.id} className="flex justify-between text-sm pb-2 border-b border-white/5">
                <span className="text-[#FAF9F6]/80">{p.name}</span>
                <span className="text-[#E8A87C] font-semibold">{p.stock} {p.unit}</span>
              </li>
            ))}
            {(d.low_stock || []).length === 0 && <li className="text-[#FAF9F6]/40 text-sm">All stocked up ✓</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
